import { useRef, useState, useEffect, useCallback } from 'react';
import { HomeLayout } from '../layouts/HomeLayout';
import { IdentityCard } from '../components/home/IdentityCard';
import { InfoCard } from '../components/home/InfoCard';
import { SkillsCard } from '../components/home/SkillsCard';
import { NavCard } from '../components/home/NavCard';
import { SEOHead } from '../components/seo/SEOHead';
import { SandstormBackground, DEFAULT_SANDSTORM_CONFIG } from '../components/layout/SandstormBackground';
import { SandstormDebugPanel } from '../components/home/SandstormDebugPanel';
import { StarBurstAnimation } from '../components/animations/StarBurstAnimation';
import { MouseCursor } from '../components/ui/MouseCursor';
import { buildPersonSchema, buildWebSiteSchema, SITE } from '../utils/seo';

export function Home() {
  // configRef: read every animation tick (zero re-render cost).
  // config state: mirrors it for controlled slider UI.
  const configRef = useRef({ ...DEFAULT_SANDSTORM_CONFIG });
  const [config, setConfig]             = useState({ ...DEFAULT_SANDSTORM_CONFIG });
  const [mouseEnabled, setMouseEnabled] = useState(true);
  const [flowsEnabled, setFlowsEnabled] = useState(true);
  const [debugOpen, setDebugOpen]       = useState(false);
  const [stats, setStats] = useState({ fps: 0, frameMs: '0.00', aliveCount: null, particleCount: null });

  // sandstormKey: incrementing triggers a full SandstormBackground remount
  // (needed for count changes, easter egg restart, etc.)
  const [sandstormKey, setSandstormKey] = useState(0);

  // Easter egg state machine: null → 'captured' → 'starburst' → null
  const [easterEggPhase, setEasterEggPhase] = useState(null);

  const mouseRef = useRef({ x: 0, y: 0, vx: 0, vy: 0, lastTime: 0 });
  const resetRef = useRef(null);

  // Update one config key in ref (immediate) and state (UI re-render)
  const handleUpdate = useCallback((key, value) => {
    configRef.current = { ...configRef.current, [key]: value };
    setConfig(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleToggleMouse = useCallback((on) => {
    setMouseEnabled(on);
    handleUpdate('mouseEnabled', on);
  }, [handleUpdate]);

  const handleToggleFlows = useCallback((on) => {
    setFlowsEnabled(on);
    handleUpdate('enableFlows', on);
  }, [handleUpdate]);

  // Reset: full remount with current config (picks up count changes)
  const handleReset = useCallback(() => {
    setSandstormKey(k => k + 1);
    setEasterEggPhase(null);
  }, []);

  // Called by SandstormBackground when all particles are captured
  const handleAllCaptured = useCallback(() => {
    setEasterEggPhase('captured');
  }, []);

  // Called when the EasterEggMessage finishes typing
  const handleMessageDone = useCallback(() => {
    setEasterEggPhase('starburst');
  }, []);

  // Called when StarBurstAnimation ends; restarts sandstorm with fresh config
  const handleStarBurstComplete = useCallback((freshConfig) => {
    const next = freshConfig ?? { ...DEFAULT_SANDSTORM_CONFIG };
    configRef.current = { ...next, mouseEnabled: true, enableFlows: true, easterEggEnabled: debugOpen };
    setConfig(configRef.current);
    setMouseEnabled(true);
    setFlowsEnabled(true);
    setSandstormKey(k => k + 1);
    setEasterEggPhase(null);
  }, [debugOpen]);

  // Sync debugOpen → configRef.easterEggEnabled each time it changes
  useEffect(() => {
    configRef.current.easterEggEnabled = debugOpen;
  }, [debugOpen]);

  // Track mouse position + velocity while disturbance is active.
  useEffect(() => {
    if (!mouseEnabled) return;
    function onMove(e) {
      const now   = performance.now();
      const dtMs  = Math.max(now - (mouseRef.current.lastTime || now), 1);
      const rawVx = (e.clientX - mouseRef.current.x) / (dtMs / 1000);
      const rawVy = (e.clientY - mouseRef.current.y) / (dtMs / 1000);
      const alpha = 0.3;
      mouseRef.current.vx = mouseRef.current.vx * (1 - alpha) + rawVx * alpha;
      mouseRef.current.vy = mouseRef.current.vy * (1 - alpha) + rawVy * alpha;
      const cap = 1800;
      mouseRef.current.vx = Math.max(-cap, Math.min(cap, mouseRef.current.vx));
      mouseRef.current.vy = Math.max(-cap, Math.min(cap, mouseRef.current.vy));
      mouseRef.current.x        = e.clientX;
      mouseRef.current.y        = e.clientY;
      mouseRef.current.lastTime = now;
    }
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, [mouseEnabled]);

  const backgroundSlot = easterEggPhase === 'starburst' ? (
    <StarBurstAnimation
      originX={mouseRef.current.x}
      originY={mouseRef.current.y}
      color={config.color}
      count={config.count}
      onComplete={handleStarBurstComplete}
    />
  ) : (
    <SandstormBackground
      key={sandstormKey}
      configRef={configRef}
      mouseRef={mouseRef}
      resetRef={resetRef}
      onStats={setStats}
      onAllCaptured={handleAllCaptured}
    />
  );

  return (
    <>
      {/* Custom cursor — rendered outside HomeLayout so it overlays everything */}
      <MouseCursor
        enabled={mouseEnabled}
        radius={config.mouseRadius}
        color={config.color}
      />
      <HomeLayout backgroundSlot={backgroundSlot}>
        <SEOHead
          description={SITE.description}
          canonical={SITE.url}
          jsonLd={[buildWebSiteSchema(), buildPersonSchema()]}
        />

        <div className="flex-1 grid grid-rows-[1fr_auto] gap-3 p-3 sm:p-4 lg:p-5 min-h-0">

          {/* Top row */}
          <div className="grid grid-cols-1 lg:grid-cols-[5fr_3fr_4fr] gap-3 min-h-0">
            <IdentityCard
              easterEggPhase={easterEggPhase}
              onEasterEggMessageDone={handleMessageDone}
            />

            {/* Col 2: InfoCard always + SkillsCard only when debug is open */}
            <div className="hidden lg:flex lg:flex-col gap-3 min-h-0">
              <div className="shrink-0">
                <InfoCard />
              </div>
              {debugOpen && (
                <div className="flex-1 min-h-0">
                  <SkillsCard />
                </div>
              )}
            </div>

            {/* Col 3: SkillsCard normally, DebugPanel when open */}
            <div className="hidden lg:flex lg:flex-col min-h-0">
              {debugOpen ? (
                <SandstormDebugPanel
                  config={config}
                  onUpdate={handleUpdate}
                  mouseEnabled={mouseEnabled}
                  onToggleMouse={handleToggleMouse}
                  flowsEnabled={flowsEnabled}
                  onToggleFlows={handleToggleFlows}
                  stats={stats}
                  onReset={handleReset}
                />
              ) : (
                <SkillsCard />
              )}
            </div>
          </div>

          {/* Bottom nav row */}
          <nav aria-label="Site sections" className="grid grid-cols-3 gap-3 h-28 lg:h-32">
            <NavCard
              to="/projects"
              label="Projects"
              description="Open source and client work"
              icon="⬡"
              action={{
                icon: '⚙',
                label: debugOpen ? 'Close debug panel' : 'Open debug panel',
                onClick: () => setDebugOpen(v => !v),
                active: debugOpen,
              }}
            />
            <NavCard to="/articles" label="Articles" description="Notes on tech, data, and systems" icon="⬡" />
            <NavCard to="/contact"  label="Contact"  description="Get in touch" icon="⬡" />
          </nav>
        </div>
      </HomeLayout>
    </>
  );
}
